import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Pencil, Plus, Truck, AlertTriangle, Check, ExternalLink, GitMerge, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { fmtKg, fmtTs, getRouteColorClass } from './_utils';
import ReadOnlyBanner from './ReadOnlyBanner';

const ROUTE_COLOR_PRESETS = [
  { value: '#9b59b6', label: 'Purple' },
  { value: '#3498db', label: 'Blue' },
  { value: '#2ecc71', label: 'Green' },
  { value: '#f5a623', label: 'Amber' },
  { value: '#e74c3c', label: 'Red' },
  { value: '#1abc9c', label: 'Teal' },
  { value: '#64748b', label: 'Slate' },
];

export default function RouteConfig() {
  const ctx = useOutletContext() || {};
  const canEdit = ctx.canEditModule ? ctx.canEditModule('routeops_routes') : true;
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [reopt, setReopt] = useState(null);
  const [flashKey, setFlashKey] = useState(null);
  const [addingRoute, setAddingRoute] = useState(false);
  const [newRoute, setNewRoute] = useState({ name: '', color: '#64748b' });
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeData, setMergeData] = useState({ source_key: '', target_key: '' });
  const [suggestions, setSuggestions] = useState(null); // { pairs, geocoded_with, note }
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [r, f, d] = await Promise.all([
        api.get('/routeops/routes'),
        api.get('/routeops/fleet'),
        api.get('/users/drivers').catch(() => ({ data: [] })),
      ]);
      // Backend already sorts (default routes first by hardcoded order, then custom alphabetically)
      setRoutes(r.data || []);
      setFleet(f.data || []);
      setDrivers(d.data || []);
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoute = async () => {
    if (!newRoute.name.trim()) return toast.error('Route name required');
    try {
      await api.post('/routeops/routes', { name: newRoute.name.trim(), color: newRoute.color });
      toast.success('Route created');
      setAddingRoute(false);
      setNewRoute({ name: '', color: '#64748b' });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create route');
    }
  };

  const handleDeleteRoute = async (key, name) => {
    if (!window.confirm(`Delete custom route "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/routeops/routes/${key}`);
      toast.success('Route deleted');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await api.post('/routeops/routes/proximity-suggestions');
      setSuggestions(res.data);
      if (!(res.data?.pairs || []).length) {
        toast.message(res.data?.note || 'No proximity data available yet');
      }
    } catch {
      toast.error('Failed to load suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleMerge = async () => {
    if (!mergeData.source_key || !mergeData.target_key) {
      return toast.error('Pick two different routes');
    }
    if (mergeData.source_key === mergeData.target_key) {
      return toast.error('Source and target must be different');
    }
    const src = routes.find(r => r.key === mergeData.source_key);
    const tgt = routes.find(r => r.key === mergeData.target_key);
    if (!window.confirm(`Merge "${src?.name}" → "${tgt?.name}"? All bookings will be reassigned.`)) return;
    try {
      const res = await api.post('/routeops/routes/merge', mergeData);
      toast.success(`Merged ${res.data.moved || 0} booking(s) into ${tgt?.name}`);
      setMergeOpen(false);
      setMergeData({ source_key: '', target_key: '' });
      setSuggestions(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openEdit = (r) => {
    // Normalise driver_names so the editor always works on an array
    const draft = {
      ...r,
      driver_names: Array.isArray(r.driver_names) && r.driver_names.length
        ? [...r.driver_names]
        : (r.driver_name ? [r.driver_name] : []),
    };
    setEditing({ key: r.key, draft });
  };

  // When a vehicle is picked from the fleet dropdown, auto-fill type + payload
  // from the central Vehicles record so it stays in sync with the Vehicles tab.
  const handleVehicleChange = (reg) => {
    const v = fleet.find(f => f.registration === reg);
    if (v) {
      setEditing({
        ...editing,
        draft: {
          ...editing.draft,
          vehicle_reg: v.registration,
          vehicle_type: v.vehicle_type,
          max_payload_kg: v.max_payload_kg,
        },
      });
    } else {
      setEditing({
        ...editing,
        draft: { ...editing.draft, vehicle_reg: reg },
      });
    }
  };

  const handleAddDriver = () => {
    setEditing({
      ...editing,
      draft: { ...editing.draft, driver_names: [...(editing.draft.driver_names || []), ''] },
    });
  };

  const handleDriverChange = (index, value) => {
    const next = [...(editing.draft.driver_names || [])];
    next[index] = value;
    setEditing({ ...editing, draft: { ...editing.draft, driver_names: next } });
  };

  const handleRemoveDriver = (index) => {
    const next = [...(editing.draft.driver_names || [])];
    next.splice(index, 1);
    setEditing({ ...editing, draft: { ...editing.draft, driver_names: next } });
  };

  const handleSave = async () => {
    const existing = routes.find(r => r.key === editing.key);
    // Normalise drivers: trim, drop empties, dedupe (case-insensitive)
    const cleaned = (editing.draft.driver_names || [])
      .map(d => (d || '').trim())
      .filter(Boolean);
    const seen = new Set();
    const drivers = cleaned.filter(d => {
      const k = d.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const draft = { ...editing.draft, driver_names: drivers, driver_name: drivers[0] || '' };

    // Trigger reoptimise dialog when the primary driver actually changed
    if (draft.driver_name && draft.driver_name !== (existing.driver_name || '')) {
      setReopt({ route: existing, newDraft: draft });
      return;
    }
    await doSave(draft);
  };

  const doSave = async (draft, scope) => {
    try {
      const payload = {
        name: draft.name,
        driver_name: draft.driver_name || '',
        driver_names: draft.driver_names || [],
        vehicle_reg: (draft.vehicle_reg || '').toUpperCase(),
        vehicle_type: draft.vehicle_type,
        employment: draft.employment,
        ...(scope ? { reoptimise_scope: scope } : {}),
      };
      await api.put(`/routeops/routes/${draft.key}`, payload);
      toast.success('Route saved');
      setFlashKey(draft.key);
      setTimeout(() => setFlashKey(null), 1000);
      setEditing(null);
      setReopt(null);
      fetchAll();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const handleManageVehicles = () => {
    navigate('/vehicles');
  };

  return (
    <div data-testid="routeops-routes-page">
      <div className="flex items-center gap-3 pb-3 mb-4 border-b border-slate-200">
        <h1 className="text-2xl font-bold text-slate-900">Route Configuration</h1>
        <span className="text-xs text-slate-500 uppercase tracking-wide">Driver &amp; Vehicle Assignment</span>
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 ml-2">
          {routes.filter(r => r.driver_name).length}/{routes.length} Routes Active
        </Badge>
        {canEdit && (
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setMergeOpen(true); fetchSuggestions(); }}
              data-testid="merge-routes-btn"
            >
              <GitMerge className="h-3.5 w-3.5 mr-1" />
              Merge Routes
            </Button>
            <Button
              size="sm"
              onClick={() => setAddingRoute(true)}
              data-testid="add-route-btn"
              style={{ background: 'var(--amber, #f5a623)', color: '#0d1117' }}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Route
            </Button>
          </div>
        )}
      </div>

      {!canEdit && <ReadOnlyBanner pageName="Route Configuration" />}

      <div className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Active Routes</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
        {routes.map(r => {
          const editingThis = editing?.key === r.key;
          const d = editingThis ? editing.draft : r;
          const noDriver = !(r.driver_name || (Array.isArray(r.driver_names) && r.driver_names.length > 0));
          return (
            <Card
              key={r.key}
              className={`transition-all ${flashKey === r.key ? 'ring-2 ring-emerald-500' : ''} ${
                noDriver ? 'border-red-200' : ''
              }`}
              data-testid={`route-card-${r.key}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-3 h-3 rounded-full ${getRouteColorClass(r.key)}`} />
                  {editingThis ? (
                    <Input
                      value={d.name}
                      onChange={e => setEditing({ ...editing, draft: { ...d, name: e.target.value } })}
                      className="h-7 text-base font-bold flex-1"
                    />
                  ) : (
                    <h3 className="font-bold text-slate-900 flex-1">{r.name}</h3>
                  )}
                  {r.key.startsWith('hgv') && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">HGV</Badge>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  {/* Drivers — multiple drivers can share one route */}
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-slate-500 w-28 uppercase tracking-wide pt-1">Drivers</span>
                    <div className="flex-1 space-y-1">
                      {editingThis ? (
                        <>
                          {(d.driver_names && d.driver_names.length > 0 ? d.driver_names : ['']).map((name, i) => (
                            <div key={i} className="flex items-center gap-1">
                              <select
                                className="h-7 flex-1 border border-slate-200 rounded px-2 text-sm bg-white"
                                value={name}
                                onChange={(e) => handleDriverChange(i, e.target.value)}
                                data-testid={`route-driver-select-${r.key}-${i}`}
                              >
                                <option value="">— Pick a driver —</option>
                                {drivers.map(dv => (
                                  <option key={dv.id} value={dv.full_name}>
                                    {dv.full_name}
                                  </option>
                                ))}
                                {/* If the saved name isn't in the drivers list (legacy), show it as a fallback option */}
                                {name && !drivers.some(dv => dv.full_name === name) && (
                                  <option value={name}>{name} (legacy)</option>
                                )}
                              </select>
                              {i === 0 && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[9px]">
                                  Primary
                                </Badge>
                              )}
                              {(d.driver_names && d.driver_names.length > 1) && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveDriver(i)}
                                  className="text-slate-400 hover:text-red-600 px-1"
                                  title="Remove driver"
                                  data-testid={`route-driver-remove-${r.key}-${i}`}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={handleAddDriver}
                            className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            data-testid={`route-driver-add-${r.key}`}
                          >
                            <Plus className="h-3 w-3" /> Add driver
                          </button>
                          {drivers.length === 0 && (
                            <p className="text-[10px] text-slate-400">
                              No users with role "Driver" yet —
                              <button
                                type="button"
                                onClick={() => navigate('/user-management')}
                                className="text-blue-600 hover:underline ml-1"
                              >
                                add one in User Management
                              </button>
                            </p>
                          )}
                        </>
                      ) : (
                        (() => {
                          const list = (r.driver_names && r.driver_names.length) ? r.driver_names : (r.driver_name ? [r.driver_name] : []);
                          if (list.length === 0) {
                            return <span className="text-slate-400 italic">⚠ Unassigned</span>;
                          }
                          return (
                            <div className="flex flex-wrap items-center gap-1">
                              {list.map((dn, i) => (
                                <span
                                  key={i}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${
                                    i === 0 ? 'bg-amber-50 text-amber-800 border border-amber-200 font-medium' : 'bg-slate-100 text-slate-700 border border-slate-200'
                                  }`}
                                >
                                  {i === 0 && <span className="text-[8px] uppercase tracking-wide font-bold opacity-70">P</span>}
                                  {dn}
                                </span>
                              ))}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-28 uppercase tracking-wide">Vehicle Reg</span>
                    {editingThis ? (
                      <select
                        className="flex-1 h-7 border border-slate-200 rounded px-2 text-sm font-mono"
                        value={d.vehicle_reg || ''}
                        onChange={e => handleVehicleChange(e.target.value)}
                        data-testid={`route-vehicle-select-${r.key}`}
                      >
                        <option value="">— Select vehicle —</option>
                        {fleet.map(v => (
                          <option key={v.id} value={v.registration}>
                            {v.registration} · {v.vehicle_type} · {fmtKg(v.max_payload_kg)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="flex-1 font-mono text-slate-800">{r.vehicle_reg || '—'}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-28 uppercase tracking-wide">Vehicle Type</span>
                    <span className="flex-1 text-slate-700">{d.vehicle_type || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-28 uppercase tracking-wide">Max Payload</span>
                    <span className="flex-1 font-mono font-bold text-amber-600">{fmtKg(d.max_payload_kg)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-28 uppercase tracking-wide">Type</span>
                    {editingThis ? (
                      <select
                        className="flex-1 h-7 border border-slate-200 rounded px-2 text-sm"
                        value={d.employment}
                        onChange={e => setEditing({ ...editing, draft: { ...d, employment: e.target.value } })}
                      >
                        <option value="In-House">In-House</option>
                        <option value="Agency">Agency</option>
                      </select>
                    ) : (
                      <Badge variant="outline" className={`${r.employment === 'In-House' ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                        {r.employment}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                  <div className="text-xs text-slate-400">
                    {noDriver ? (
                      <span className="text-red-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> No driver assigned</span>
                    ) : (
                      <>Updated {fmtTs(r.updated_at)}</>
                    )}
                  </div>
                  {editingThis ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                      <Button size="sm" onClick={handleSave} data-testid={`route-save-${r.key}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Save
                      </Button>
                    </div>
                  ) : canEdit ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`route-edit-${r.key}`}>
                        {noDriver ? <><Plus className="h-3.5 w-3.5 mr-1" /> Assign Driver</> : <><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</>}
                      </Button>
                      {r.is_custom && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-50 hover:text-red-700"
                          onClick={() => handleDeleteRoute(r.key, r.name)}
                          data-testid={`route-delete-${r.key}`}
                          title="Delete custom route"
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Fleet Register — sourced from the central /vehicles list */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-bold">
          Fleet Register
          <span className="text-[10px] ml-2 normal-case font-normal text-slate-400">
            Synced from the Vehicles tab
          </span>
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleManageVehicles}
            className="h-7 text-[11px]"
            data-testid="fleet-manage-vehicles"
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            Manage in Vehicles tab
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="fleet-register">
        {fleet.length === 0 && (
          <Card className="col-span-full border-dashed">
            <CardContent className="p-4 text-center text-sm text-slate-500">
              No vehicles yet. Add them from the
              {' '}
              <button onClick={handleManageVehicles} className="text-blue-600 underline font-medium">
                Vehicles tab
              </button>
              .
            </CardContent>
          </Card>
        )}
        {fleet.map(v => (
          <Card key={v.id} className="hover:shadow-md transition">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                <Truck className="h-5 w-5 text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono font-bold text-sm">{v.registration}</div>
                <div className="text-xs text-slate-500 truncate">{v.vehicle_type}</div>
              </div>
              <div className="text-right">
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-mono text-[10px]">
                  {fmtKg(v.max_payload_kg)}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
        {canEdit && fleet.length > 0 && (
          <Card
            className="border-dashed cursor-pointer hover:border-blue-400 hover:bg-blue-50/50"
            onClick={handleManageVehicles}
            data-testid="fleet-add-vehicle"
          >
            <CardContent className="p-3 flex items-center justify-center text-slate-500 font-semibold gap-1">
              <Plus className="h-4 w-4" />
              <span>Add via Vehicles tab</span>
              <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Reoptimise modal */}
      <Dialog open={!!reopt} onOpenChange={() => setReopt(null)}>
        <DialogContent data-testid="reoptimise-modal">
          <DialogHeader>
            <DialogTitle>Reoptimise Route</DialogTitle>
          </DialogHeader>
          {reopt && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                {reopt.route.name} — {reopt.newDraft.vehicle_reg}
              </div>
              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="flex-1">
                  <div className="text-[10px] uppercase text-slate-500 tracking-wide">Previous Driver</div>
                  <div className="font-semibold">{reopt.route.driver_name || '(unassigned)'}</div>
                </div>
                <div className="text-2xl text-slate-300">→</div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase text-slate-500 tracking-wide">New Driver</div>
                  <div className="font-semibold text-emerald-700">{reopt.newDraft.driver_name}</div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase font-bold text-slate-500 mb-2">Apply change to</div>
                <div className="space-y-2">
                  {[
                    { k: 'today', t: 'Today Only', d: "Update driver on today's active deliveries only." },
                    { k: 'week', t: 'This Week', d: 'Reassign all planned deliveries this week to the new driver.' },
                    { k: 'all', t: 'All Future Bookings', d: 'Permanently reassign this route to the new driver going forward.' },
                  ].map(opt => (
                    <button
                      key={opt.k}
                      onClick={() => doSave(reopt.newDraft, opt.k)}
                      className="w-full text-left border border-slate-200 rounded-lg p-3 hover:border-blue-400 hover:bg-blue-50/50"
                      data-testid={`reopt-scope-${opt.k}`}
                    >
                      <div className="font-semibold text-slate-800">{opt.t}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{opt.d}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" onClick={() => setReopt(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Route modal */}
      <Dialog open={addingRoute} onOpenChange={setAddingRoute}>
        <DialogContent data-testid="add-route-modal">
          <DialogHeader>
            <DialogTitle>Add Custom Route</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Route Name</Label>
              <Input
                value={newRoute.name}
                onChange={(e) => setNewRoute({ ...newRoute, name: e.target.value })}
                placeholder="e.g. Midlands Express"
                data-testid="new-route-name"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Color</Label>
              <div className="flex flex-wrap gap-1.5">
                {ROUTE_COLOR_PRESETS.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setNewRoute({ ...newRoute, color: c.value })}
                    className={`w-8 h-8 rounded-md border-2 transition-all ${newRoute.color === c.value ? 'border-slate-900 scale-110' : 'border-transparent hover:border-slate-300'}`}
                    style={{ background: c.value }}
                    title={c.label}
                    data-testid={`new-route-color-${c.value.replace('#','')}`}
                  />
                ))}
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              You can assign drivers and vehicles after creating the route.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setAddingRoute(false)}>Cancel</Button>
              <Button
                onClick={handleCreateRoute}
                data-testid="create-route-confirm"
                style={{ background: 'var(--amber, #f5a623)', color: '#0d1117' }}
              >
                Create Route
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge Routes modal */}
      <Dialog open={mergeOpen} onOpenChange={(o) => { setMergeOpen(o); if (!o) { setSuggestions(null); setMergeData({ source_key: '', target_key: '' }); } }}>
        <DialogContent data-testid="merge-routes-modal" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-blue-600" />
              Merge Routes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              All bookings on the <strong>source</strong> route will be reassigned to the <strong>target</strong> route. Custom routes are deleted; default routes are kept but emptied.
            </p>

            {/* AI suggestions panel */}
            <div className="border rounded-lg p-3 bg-blue-50/40 border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-bold uppercase tracking-wide text-blue-700">
                  AI Proximity Suggestions
                </span>
                {suggestions?.geocoded_with === 'google' && (
                  <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300 text-[9px]">
                    via Google Maps
                  </Badge>
                )}
              </div>
              {loadingSuggestions ? (
                <p className="text-xs text-slate-500">Geocoding postcodes...</p>
              ) : !suggestions ? (
                <Button size="sm" variant="outline" onClick={fetchSuggestions} data-testid="fetch-suggestions-btn">
                  Get suggestions
                </Button>
              ) : (suggestions.pairs || []).length === 0 ? (
                <p className="text-xs text-slate-500">{suggestions.note || 'No suggestions available.'}</p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {suggestions.pairs.map((p, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setMergeData({ source_key: p.route_b.key, target_key: p.route_a.key })}
                      className="w-full text-left flex items-center gap-2 text-xs hover:bg-blue-100 rounded px-2 py-1.5 transition"
                      data-testid={`suggestion-${idx}`}
                    >
                      <span className="font-mono text-blue-700 font-bold">{p.distance_km} km</span>
                      <span className="text-slate-700 flex-1">
                        <strong>{p.route_a.name}</strong> ↔ <strong>{p.route_b.name}</strong>
                      </span>
                      <span className="text-[10px] text-blue-600 font-bold uppercase">Use</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Source (will be merged)</Label>
                <select
                  className="w-full h-9 border border-slate-200 rounded px-2 text-sm bg-white"
                  value={mergeData.source_key}
                  onChange={(e) => setMergeData({ ...mergeData, source_key: e.target.value })}
                  data-testid="merge-source-select"
                >
                  <option value="">— Pick source —</option>
                  {routes.map(r => (
                    <option key={r.key} value={r.key} disabled={r.key === mergeData.target_key}>
                      {r.name} {r.is_custom ? '(custom)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Target (keeps everything)</Label>
                <select
                  className="w-full h-9 border border-slate-200 rounded px-2 text-sm bg-white"
                  value={mergeData.target_key}
                  onChange={(e) => setMergeData({ ...mergeData, target_key: e.target.value })}
                  data-testid="merge-target-select"
                >
                  <option value="">— Pick target —</option>
                  {routes.map(r => (
                    <option key={r.key} value={r.key} disabled={r.key === mergeData.source_key}>
                      {r.name} {r.is_custom ? '(custom)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setMergeOpen(false)}>Cancel</Button>
              <Button
                onClick={handleMerge}
                disabled={!mergeData.source_key || !mergeData.target_key}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="merge-confirm-btn"
              >
                <GitMerge className="h-3.5 w-3.5 mr-1" />
                Merge
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
